/**
 * Center stage — tab bar + active tab content area.
 *
 * Phase 0: tab bar wired, every tab body is a placeholder card noting
 * the phase that will fill it. The Schedule tab gets a slightly more
 * complete teaser since it's first up in Phase 1.
 */
import { html, type TemplateResult } from "lit-html";
import type { TabId } from "../App";
import { renderScheduleGantt } from "./ScheduleGantt";
import { renderPipelineFlow } from "./PipelineFlow";
import { renderCalibrationLab } from "./CalibrationLab";
import { renderBetInspector } from "./BetInspector";
import { renderCostConsole } from "./CostConsole";
import { renderPromptWorkbench } from "./PromptWorkbench";
import { renderStrategyConsole } from "./StrategyConsole";
import { renderSlateReplay } from "./SlateReplay";
import { renderRewind } from "./Rewind";
import { renderHrRotation } from "./HrRotation";
import { renderAlertsConsole } from "./AlertsConsole";
import { renderLineupDiary } from "./LineupDiary";
import { renderExperiments } from "./Experiments";
import { renderMobilePreview } from "./MobilePreview";
import { renderEasterEggs } from "./EasterEggs";
import { renderTodoSurface } from "./TodoSurface";
import { renderDashboardDiffViewer } from "./DashboardDiffViewer";

interface CenterStageProps {
  tabs: { id: TabId; label: string; phase: number }[];
  activeTab: TabId;
  onTabChange: (id: TabId) => void;
}

export function renderCenterStage(props: CenterStageProps): TemplateResult {
  return html`
    <main class="center-stage">
      <nav class="tab-bar" role="tablist">
        ${props.tabs.map(
          (t) => html`
            <button
              class="tab-bar__tab ${t.id === props.activeTab ? "is-active" : ""}"
              @click=${() => props.onTabChange(t.id)}
              role="tab"
              aria-selected=${t.id === props.activeTab}
            >
              ${t.label}
              <span style="
                margin-left:6px;font-size:9px;font-weight:600;
                color:var(--text-quaternary);letter-spacing:0.06em;">
                P${t.phase}
              </span>
            </button>
          `
        )}
      </nav>
      <div class="tab-content">
        ${renderActiveTab(props.activeTab, () => props.onTabChange(props.activeTab))}
      </div>
    </main>
  `;
}

function renderActiveTab(id: TabId, onChange: () => void): TemplateResult {
  switch (id) {
    case "schedule":    return renderScheduleGantt(onChange);
    case "pipeline":    return renderPipelineFlow(onChange);
    case "calibration": return renderCalibrationLab(onChange);
    case "bets":        return renderBetInspector(onChange);
    case "cost":        return renderCostConsole(onChange);
    case "prompts":     return renderPromptWorkbench(onChange);
    case "strategies":  return renderStrategyConsole(onChange);
    case "replay":      return renderSlateReplay(onChange);
    case "rewind":      return renderRewind(onChange);
    case "rotation":    return renderHrRotation(onChange);
    case "alerts":      return renderAlertsConsole(onChange);
    case "lineups":     return renderLineupDiary(onChange);
    case "experiments": return renderExperiments(onChange);
    case "preview":     return renderMobilePreview(onChange);
    case "eggs":        return renderEasterEggs(onChange);
    case "todo":        return renderTodoSurface(onChange);
    case "diff":        return renderDashboardDiffViewer(onChange);
    default:            return placeholderTab(id);
  }
}

function placeholderTab(id: TabId): TemplateResult {
  return html`
    <div class="panel" style="min-height:300px;display:flex;
                              align-items:center;justify-content:center;
                              flex-direction:column;gap:12px;">
      <div style="font-size:14px;color:var(--text-tertiary);">
        ${id} <span class="phase-stub-badge">future phase</span>
      </div>
      <div style="font-size:12px;color:var(--text-quaternary);text-align:center;
                  max-width:480px;line-height:1.5;">
        This pane lights up in a later build phase per
        <code style="color:var(--text-secondary);">CONSOLE-BUILD-PLAN.md</code>.
      </div>
    </div>
  `;
}
